<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="article-autocomplete-container" :class="{ isCollapsed }">
    <div class="user-input-section">
      <div v-if="title" style="display: flex; justify-content: space-between; align-items: center; width: 100%">
        <h1 class="article-autocomplete-title">
          {{ title }}
        </h1>
        <div class="collapse-arrow-container" @click="toggleCollapsed">
          <SearchIcon
            v-if="isCollapsed"
            v-track-click="{
              name: `${trackEventContext}${BTN_EVTS.SEARCH_ARTICLE}`,
              selection: trackEventSelection,
            }"
          />
          <HeadArrowUpIcon v-else />
        </div>
      </div>
      <div class="autocomplete-input-container">
        <SearchIcon width="16px" class="search-icon" />
        <input
          ref="inputRef"
          v-model="query"
          v-track-click="{
            name: `${trackEventContext}${BTN_EVTS.SEARCH_ARTICLE}`,
            selection: trackEventSelection,
          }"
          enterkeyhint="Search"
          :maxlength="MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM"
          :placeholder="$t('component.article_autocomplete.input_placeholder')"
          @input="setSearchTextFromEvent"
          @focus="changeShowingDropdown(true)"
          @blur="changeShowingDropdown(false)"
        />
        <div v-if="query.length > 0" class="clear-input-icon" @click="clearInput">
          <ClearIcon alt="Clear Icon" />
        </div>
      </div>
    </div>

    <div class="autocomplete-dropdown-wrapper">
      <div
        v-if="showDropdown && !isCollapsed"
        v-waiting.center.transparent="isLoading = { isLoading: loading }"
        class="autocomplete-dropdown"
        :class="{ isLoading: loading, noData: isEmpty }"
      >
        <span v-if="!loading && !validQueryText" class="recent-searches-title">
          {{ $t('component.article_autocomplete.recent_search') }}
        </span>
        <ul v-if="!isEmpty">
          <ArticleListItem
            v-for="item in dropdownData"
            :key="item.id"
            :item="item"
            :highlight-query="query"
            @mousedown="openArticle(item)"
          />
        </ul>
        <div v-else class="no-data">
          <span v-if="!validQueryText && isEmpty">
            {{ $t('component.article_autocomplete.no_historical_data') }}
          </span>
          <span v-else>
            {{ $t('component.article_autocomplete.no_data') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import * as PleasedApi from '@/common/api/api.pleased';
import { SearchArticle } from '@/common/api/models.pleased';
import waiting from '@/components/loading/waiting';
import { emitOpenArticle, onArticleAutocompleteFocused, onArticleAutocompleteCollapse } from '@/common/eventBus';
import { debounce, markSearchTextToHtml } from '@/vendor/pleased-sdk/util';
import ArticleListItem from './ArticleListItem.vue';
import AttachmentIcon from './icons/AttachmentIcon.vue';
import SearchIcon from './icons/SearchIcon.vue';
import HeadArrowUpIcon from './icons/HeadArrowUpIcon.vue';
import ClearIcon from './icons/ClearIcon.vue';

import * as Locale from '@/plugins/i18n';
import {
  LETTERS_DIGITS_SPACES_REGEXP,
  MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM,
  MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH,
} from '@/common/config';
import { PleasedUserInfo } from '@/common/types';
import { reportingService } from '@/common/reporting';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics } from '@/common/logs/EventLogger';

const userModule = namespace('user');
const articleAutocompleteModule = namespace('articleAutocomplete');

@Component({
  components: { ArticleListItem, AttachmentIcon, SearchIcon, HeadArrowUpIcon, ClearIcon },
  directives: {
    waiting,
    trackClick,
  },
})
export default class ArticleAutocomplete extends Vue {
  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;

  @articleAutocompleteModule.State('searchHistory') searchHistory!: { [key: string]: SearchArticle[] };
  @articleAutocompleteModule.Mutation addSearchHistoryItem!: (payload: {
    searchHistoryItem: SearchArticle;
    userId?: number | string;
  }) => void;

  @Prop({ default: null }) readonly title!: string | null;
  @Prop(Boolean) readonly defaultCollapsed!: boolean;
  @Prop({ type: String, default: '' }) readonly trackEventContext!: string;
  @Prop(Number) readonly trackEventSelection!: number;

  readonly MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM = MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM;
  readonly MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH = MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH;
  readonly BTN_EVTS = BTN_EVTS;

  declare $refs: Vue['$refs'] & { inputRef: HTMLInputElement };

  public isCollapsed: boolean = false;
  public query: string = '';
  public showDropdown: boolean = false;
  public loading: boolean = false;
  public searchData: SearchArticle[] = [];

  cleanUps: Function[] = [];

  debouncedFetchSearchData = debounce(this.fetchArticles, 500);

  get isEmpty(): boolean {
    return this.dropdownData.length === 0;
  }

  get dropdownData(): SearchArticle[] {
    const currentLang = Locale.i18n.locale as Locale.SupportedLanguages;
    const key = `${this.pleasedUser.userId}-${currentLang}`;

    const searchedData = this.searchHistory[key] || [];
    return this.validQueryText ? this.searchData : searchedData;
  }

  get validQueryText() {
    const searchText = this.query?.trim();

    return searchText && searchText.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH;
  }

  getArticleItemTrackParams(article: SearchArticle) {
    const eventName = this.validQueryText
      ? BTN_EVTS.SEARCH_ARTICLE_RESULT
      : BTN_EVTS.SEARCH_ARTICLE_RECENT_SEARCH_ARTICLE;

    return {
      name: `${this.trackEventContext}${eventName}`,
      selection: article.id,
    };
  }

  created() {
    this.isCollapsed = this.defaultCollapsed;

    this.cleanUps.push(
      onArticleAutocompleteFocused((isFocused) => {
        if (isFocused) {
          this.$refs.inputRef.focus();
        }
      }),
      onArticleAutocompleteCollapse((isCollapsed) => {
        this.isCollapsed = isCollapsed;
      })
    );
  }

  async fetchArticles(): Promise<void> {
    const searchText = this.query?.trim();

    // The query can shrink below the minimum while the debounced call is still pending
    // (mobile browsers also delay timers of a background page), so re-check it here.
    if (!searchText || searchText.length < MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH) {
      this.loading = false;
      return;
    }

    try {
      const articles = await PleasedApi.getArticleSearch.cached({ searchText });
      this.searchData = articles.data;
    } catch {
      // API failures are already reported by the api layer; the dropdown just shows no data.
      this.searchData = [];
    } finally {
      this.loading = false;
    }
  }

  beforeDestroy() {
    this.debouncedFetchSearchData.cancel();

    this.cleanUps?.forEach((c) => {
      try {
        c?.();
      } finally {
      }
    });
  }

  setSearchTextFromEvent(e: Event): void {
    const target = e.target as HTMLInputElement;

    let value = target.value;

    value = value.replace(LETTERS_DIGITS_SPACES_REGEXP, '');

    if (value.length > MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM) {
      value = value.substring(0, MAX_ARTICLE_AUTOCOMPLETE_CHAR_NUM);
    }

    this.query = value;

    const trimmedValue = value?.trim();

    if (trimmedValue && trimmedValue.length >= MIN_ARTICLE_AUTOCOMPLETE_TEXT_LENGTH) {
      this.loading = true;
      this.debouncedFetchSearchData();
    } else {
      this.debouncedFetchSearchData.cancel();
      this.loading = false;
      this.searchData = [];
    }
  }

  openArticle(article: SearchArticle): void {
    reportingService.selectedArticleFromSearch(article.id);
    analytics.trackClick(this.getArticleItemTrackParams(article));
    PleasedApi.saveArticleHit({ articleId: article.id, searchText: this.query });

    this.addSearchHistoryItem({ searchHistoryItem: article, userId: this.pleasedUser.userId });

    emitOpenArticle({
      isOpen: true,
      articleId: article.id,
      fetchArticle: true,
      isFromAutocomplete: true,
      categoryName: article.categoryName,
    });
  }

  changeShowingDropdown(value: boolean) {
    this.showDropdown = value;
  }

  clearInput() {
    this.debouncedFetchSearchData.cancel();
    this.query = '';
    this.loading = false;
    this.searchData = [];
  }

  highlightText(title: string) {
    return markSearchTextToHtml(title, this.query.trim());
  }

  toggleCollapsed() {
    this.isCollapsed = !this.isCollapsed;

    if (this.isCollapsed) {
      this.showDropdown = false;
    } else {
      this.$refs.inputRef.focus();
    }
  }

  @Watch('defaultCollapsed')
  onDefaultCollapsedChange(val: boolean) {
    this.isCollapsed = val;
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

.article-autocomplete-container {
  position: relative;
  background: $article-autocomplete-background;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 20px;
  transition: all 0.2s ease-in-out;

  .user-input-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .collapse-arrow-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    transition: all 0.2s ease-in-out;
    color: $article-autocomplete-collapse-icon-color;
  }

  .article-autocomplete-title {
    color: $article-autocomplete-title-color;
    font-size: 20px;
    font-weight: bold;
  }

  .autocomplete-dropdown-wrapper {
    width: 100%;
    height: 2px;
    position: relative;
  }

  .autocomplete-input-container {
    position: relative;
    width: 100%;

    .search-icon {
      position: absolute;
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
      color: $article-autocomplete-input-placeholder-color;
    }

    .clear-input-icon {
      display: flex;
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
      color: $article-autocomplete-clear-icon-color;
    }
  }

  input {
    width: 100%;
    height: 32px;
    padding: 0 8px;
    font-size: 14px;
    border-radius: 4px;
    padding-left: 32px;
    padding-right: 32px;
    border: none;
    color: $article-autocomplete-input-color;
    background: $article-autocomplete-input-background;

    &::placeholder {
      font-size: 14px;
      color: $article-autocomplete-input-placeholder-color;
    }
  }
}

.article-autocomplete-container.isCollapsed {
  padding: 12px 16px;
  transition: all 0.2s ease-in-out;

  .user-input-section {
    gap: 0px;
  }

  .collapse-arrow-container {
    width: 32px;
    height: 32px;
    color: $article-autocomplete-search-icon-color;
    background: $article-autocomplete-collapse-block-background;
    border-radius: 4px;
  }

  .article-autocomplete-title {
    font-size: 16px;
  }

  .autocomplete-input-container {
    opacity: 0;
    height: 0px;
  }
}

.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  padding: 16px 0;
  border-radius: 4px;
  background-color: $article-autocomplete-dropdown-background;
  // box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
  box-shadow:
    rgba(0, 0, 0, 0.3) 0px 19px 38px,
    rgba(0, 0, 0, 0.22) 0px 15px 12px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;

  .loading {
    margin-bottom: 0px;
  }

  &.isLoading {
    min-height: 100px;
  }

  .recent-searches-title {
    font-size: 14px;
    font-weight: bold;
    color: $article-autocomplete-dropdown-recent-searches-color;
    padding: 0 16px 12px;
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
    transition: all 0.2s ease-in-out;
    opacity: 1;
  }

  &.isLoading ul,
  &.isLoading .no-data {
    opacity: 0;
    transition: all 0.2s ease-in-out;
  }

  .no-data {
    display: flex;
    align-items: center;
    flex: 1;

    padding: 0 16px;
    font-size: 16px;
    color: $article-autocomplete-dropdown-no-data-color;
  }
}
</style>
